// src/modules/orion-shell/SidebarChats.tsx
//
// Panel «Chats» de la barra lateral del shell Orion: historial del usuario
// (GET /threads/me/list) agrupado por fecha, con búsqueda. Abrir un chat no
// obliga a pasar por el Inicio ni a crear uno nuevo (Samuel, 23-oct).
import React, { useEffect, useMemo, useState } from 'react';
import { MessageSquare, Pencil, Search } from 'lucide-react';
import { getMyThreads, renameThread, threadDisplayTitle } from '@/lib/api';
import { RenameInput } from '@/components/assistant/ThreadTitle';

type Thread = { id: string; title?: string | null; last_message_preview?: string | null; last_message_at?: string | null; updated_at?: string | null; created_at?: string | null; read_only?: boolean };

// /threads/me/list devuelve `thread_id` (no `id`): sin esto cada clic abría ?hilo=undefined.
function toThreads(raw: unknown): Thread[] {
  return ((Array.isArray(raw) ? raw : []) as Array<Thread & { thread_id?: string }>)
    .map((t) => ({ ...t, id: String(t.id ?? t.thread_id ?? '') }))
    .filter((t) => t.id && !t.read_only);
}

function when(t: Thread): number {
  const s = t.last_message_at || t.updated_at || t.created_at;
  const n = s ? Date.parse(s) : NaN;
  return Number.isFinite(n) ? n : 0;
}

function group(ts: number, now: number): string {
  if (!ts) return 'Antes';
  const d0 = new Date(now); d0.setHours(0, 0, 0, 0);
  if (ts >= d0.getTime()) return 'Hoy';
  if (ts >= d0.getTime() - 6 * 864e5) return 'Últimos 7 días';
  return 'Antes';
}

export function SidebarChats({ activeId, refreshKey, onOpen }: {
  activeId: string | null;
  refreshKey: string;
  onOpen: (id: string) => void;
}) {
  const [threads, setThreads] = useState<Thread[] | null>(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<string | null>(null);

  // Renombrar desde la barra (doble clic o lápiz). Optimista: si falla, se recarga la lista.
  const rename = async (id: string, title: string) => {
    const clean = title.trim();
    setEditing(null);
    if (!clean) return;
    setThreads((old) => old?.map((t) => (t.id === id ? { ...t, title: clean } : t)) ?? old);
    try {
      await renameThread(id, clean);
      window.dispatchEvent(new CustomEvent('orion-thread-renamed', { detail: { id, title: clean } }));
    } catch {
      getMyThreads().then((r) => setThreads(toThreads(r?.threads))).catch(() => {});
    }
  };

  useEffect(() => {
    let alive = true;
    getMyThreads()
      .then((r) => { if (alive) { setThreads(toThreads(r?.threads)); setError(false); } })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [refreshKey]);

  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ id: string; title: string }>).detail;
      if (d?.id) setThreads((old) => old?.map((t) => (t.id === d.id ? { ...t, title: d.title } : t)) ?? old);
    };
    window.addEventListener('orion-thread-renamed', on);
    return () => window.removeEventListener('orion-thread-renamed', on);
  }, []);

  const grouped = useMemo(() => {
    const now = Date.now();
    const term = q.trim().toLowerCase();
    const list = (threads || [])
      .filter((t) => !term || threadDisplayTitle(t).toLowerCase().includes(term))
      .sort((a, b) => when(b) - when(a));
    const out: { label: string; items: Thread[] }[] = [];
    for (const t of list) {
      const g = group(when(t), now);
      let bucket = out.find((x) => x.label === g);
      if (!bucket) { bucket = { label: g, items: [] }; out.push(bucket); }
      bucket.items.push(t);
    }
    return out;
  }, [threads, q]);

  return (
    <div className="-mx-1 flex min-h-0 flex-1 flex-col px-1">
      <label className="relative mb-2 block">
        <span className="sr-only">Buscar en tus chats</span>
        <Search size={14} aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--sidebar-muted)]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar en tus chats"
          className="w-full rounded-lg border border-[color:var(--sidebar-hover)] bg-transparent py-1.5 pl-8 pr-2 text-[13px] text-[color:var(--sidebar-fg)] placeholder:text-[color:var(--sidebar-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--acc1)]"
        />
      </label>
      <nav aria-label="Tus chats" className="flex-1 overflow-y-auto">
        {threads === null && !error && (
          <ul className="flex flex-col gap-1" aria-hidden>
            {[0, 1, 2, 3].map((i) => <li key={i} className="h-8 animate-pulse rounded-lg bg-[color:var(--sidebar-hover)]" />)}
          </ul>
        )}
        {error && <p className="px-2 text-[13px] text-[color:var(--sidebar-muted)]">No pude cargar tus chats.</p>}
        {threads !== null && grouped.length === 0 && (
          <p className="px-2 text-[13px] text-[color:var(--sidebar-muted)]">{q ? 'Ningún chat coincide.' : 'Aún no tienes chats. Empieza uno con «Nuevo chat».'}</p>
        )}
        {grouped.map((g) => (
          <div key={g.label} className="mb-3">
            <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--sidebar-muted)]">{g.label}</p>
            <ul className="flex flex-col gap-0.5">
              {g.items.map((t) => {
                const active = t.id === activeId;
                if (editing === t.id) {
                  return (
                    <li key={t.id} className="px-1 py-0.5">
                      <RenameInput
                        initial={t.title || threadDisplayTitle(t)}
                        onSave={(v) => void rename(t.id, v)}
                        onCancel={() => setEditing(null)}
                        className="w-full rounded-lg border border-[color:var(--sidebar-border)] bg-transparent px-2 py-1 text-[13px] text-[color:var(--sidebar-fg)] focus:outline-none focus:ring-2 focus:ring-[color:var(--acc1)]"
                      />
                    </li>
                  );
                }
                return (
                  <li key={t.id} className="group relative">
                    <button
                      type="button"
                      onClick={() => onOpen(t.id)}
                      onDoubleClick={() => setEditing(t.id)}
                      aria-current={active ? 'page' : undefined}
                      className={`flex w-full items-center gap-2 rounded-lg py-1.5 pl-2 pr-8 text-left text-[13px] transition-colors ${active ? 'bg-[color:var(--sidebar-hover)] text-[color:var(--sidebar-fg)]' : 'text-[color:var(--sidebar-muted)] hover:bg-[color:var(--sidebar-hover)] hover:text-[color:var(--sidebar-fg)]'}`}
                    >
                      <MessageSquare size={14} aria-hidden className="shrink-0" />
                      <span className="truncate">{threadDisplayTitle(t)}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(t.id)}
                      aria-label={`Renombrar «${threadDisplayTitle(t)}»`}
                      title="Renombrar"
                      className="absolute right-1 top-1/2 -translate-y-1/2 rounded-md p-1 text-[color:var(--sidebar-muted)] opacity-100 hover:text-[color:var(--sidebar-fg)] focus:opacity-100 md:opacity-0 md:group-hover:opacity-100"
                    >
                      <Pencil size={13} aria-hidden />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </div>
  );
}
